/**
 * Lane C0-OUTFIT2X: hair painted natively at the figure grid. The style's shape and its strand lines come from the
 * drawn hairstyle (hair.ts, per view, with its sway); the light is painted anew at full resolution: a soft shadow
 * side and a deep shadow under what lies over it, the strands as darker lines, and a sheen band across the crown
 * on the lit side (the anime "halo"), broken by the strands — lit from the side the frame will be seen from.
 * A man's style can drop its ribbons (painted as hair).
 */
import { hairLayer } from '../figure';
import type { Layer, Piece } from '../figure';
import type { NativeCtx } from './native';
import { above, below, makeAcross, mendStandIns, mat, occlusion, rgba, Roles, sentinel, tone } from './paint';

/** The ribbon colours drawn into lit styles (figure.ts RIBBON). */
const RIBBON = new Set([rgba('#b8323c'), rgba('#7c1e2a')]);

export interface HairOpts {
  /** Paint the style's ribbons as hair (a man wears none). */
  noRibbon?: boolean;
}

/** A hairstyle layer painted natively. */
export function nativeHairLayer(style: string, hair: string, o: HairOpts = {}): Layer {
  const s = sentinel('hair', hair);
  const roles = new Roles([['hair', s]]);
  const m = mat(hair, 'hair');
  const base = hairLayer(style, s);
  return {
    ...base,
    native: true,
    pieces: (sk, fit) =>
      base.pieces(sk, fit).map((p): Piece => {
        // Which part: the cap sits just over the head (z + 0.02); locks, bun and tail are the loose parts.
        const cap = Math.abs(p.z - (sk.joints.head.z + 0.02)) < 1e-6;
        return { ...p, native: (c) => paintHair(c, roles, m.ramp, cap, o) };
      }),
  };
}

function paintHair(c: NativeCtx, roles: Roles, r: readonly number[], cap: boolean, o: HairOpts): void {
  const ramp = r as unknown as Parameters<typeof tone>[0];
  const across = makeAcross(c, 'head');
  const painted = new Uint8Array(c.bw * c.bh);
  for (let y = c.by; y < c.by + c.bh; y++)
    for (let x = c.bx; x < c.bx + c.bw; x++) {
      if (!c.vis(x, y)) continue;
      const p = c.at(x, y);
      let hit = roles.hit(p);
      if (!hit && o.noRibbon && RIBBON.has(p)) hit = { role: 'hair', dl: 0 };
      if (!hit) hit = roles.hit(c.src(x, y));
      if (!hit) continue;
      const strand = hit.dl < -0.02 && hit.dl > -0.09;
      const a = across(x, y);
      let lv = 0;
      if (a.t < 0.24 && a.w >= 5) lv = -1;
      if (a.dark === 0 && a.w >= 8) lv = -2;
      if (strand) lv = Math.min(lv, -1);
      const up = above(c, x, y);
      // The halo: a band a few pixels under the top of the cap, on the lit half, broken by the strands.
      if (cap && up >= 4 && up <= 7 && a.t > 0.38 && a.t < 0.94 && a.w >= 8 && below(c, x, y) > 6)
        lv = strand ? 0 : up === 5 || up === 6 ? 2 : 1;
      // Loose locks and tails: a lit ridge along their light side.
      if (!cap && a.lit <= 1 && a.w >= 4 && !strand) lv = Math.max(lv, 1);
      const occ = occlusion(c, x, y);
      if (occ < 0) lv = Math.min(lv, occ);
      c.set(x, y, tone(ramp, lv));
      painted[(y - c.by) * c.bw + (x - c.bx)] = 1;
    }
  mendStandIns(c, roles, painted);
}
